import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';

interface CheckoutFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (subscriptionData: any) => void;
  selectedPlan: 'basic' | 'enterprise' | null;
  apiUrl: string;
}

const RapydCheckoutForm: React.FC<CheckoutFormProps> = ({
  open,
  onClose,
  onSuccess,
  selectedPlan,
  apiUrl
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [showManualRedirect, setShowManualRedirect] = useState(false);
  const [checkoutData, setCheckoutData] = useState<{
    id: string;
    redirectUrl: string;
    paymentPageUrl: string;
    status: string;
  } | null>(null);
  
  // Fetch updated subscription data after payment
  const fetchSubscriptionData = async () => {
    let retryCount = 0;
    const maxRetries = 3;
    let success = false;

    while (!success && retryCount <= maxRetries) {
      try {
        console.log(`Fetching subscription data (attempt ${retryCount + 1}/${maxRetries + 1})`);
        
        const response = await axios.get(
          `${apiUrl}/api/users/current`,
          { withCredentials: true }
        );
        
        if (response.data.success && response.data.user.subscription) {
          console.log('Subscription data found:', response.data.user.subscription.plan);
          setCheckoutSuccess(true);
          onSuccess(response.data.user.subscription);
          
          // Close payment dialog after successful payment
          setTimeout(() => {
            onClose();
            setCheckoutSuccess(false);
          }, 2000);
          
          success = true;
        } else {
          console.log('No subscription found in user data');
          
          // If this is the last retry and we still don't have subscription data
          if (retryCount === maxRetries) {
            setCheckoutError('Could not find subscription. Please contact support if your payment was completed.');
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 2000));
          retryCount++;
        }
      } catch (error: any) {
        console.error('Error fetching updated subscription data:', error.message);
        
        // If this is the last retry
        if (retryCount === maxRetries) {
          setCheckoutError('Error retrieving subscription. Please refresh the page or contact support.');
        }
        
        // Wait longer before retrying after an error
        await new Promise(resolve => setTimeout(resolve, 3000));
        retryCount++;
      }
    }
  };

  // Add a function to directly process a checkout
  const processCheckoutDirectly = async (checkoutId: string) => {
    try {
      console.log(`Directly processing checkout: ${checkoutId}`);
      
      const response = await axios.post(
        `${apiUrl}/api/payments/process-checkout`,
        { checkoutId },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        console.log('Checkout processed successfully:', response.data.subscription);
        setCheckoutSuccess(true);
        onSuccess(response.data.subscription);
        return true;
      } else {
        console.error('Failed to process checkout:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('Error processing checkout directly:', error);
      return false;
    }
  };

  // Update handlePaymentRedirect to check localStorage
  const handlePaymentRedirect = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionTimestamp = urlParams.get('session');
    const userId = urlParams.get('uid');
    const planType = urlParams.get('plan');
    
    // When returning from checkout, the URL will have payment=success
    if (paymentStatus === 'success' && sessionTimestamp) {
      console.log('Detected return from checkout, verifying session with params:', {
        sessionTimestamp,
        userId,
        planType
      });
      
      // Always show success UI immediately to improve user experience
      setCheckoutSuccess(true);
      
      // Clear URL parameters without page refresh
      window.history.replaceState({}, document.title, window.location.pathname);
      
      let verificationSuccess = false;
      let directProcessingSuccess = false;
      
      // First try regular verification
      try {
        const verifyResponse = await axios.post(
          `${apiUrl}/api/payments/verify-checkout-session`,
          { 
            sessionTimestamp,
            userId,
            planType 
          },
          { withCredentials: true }
        );
        
        if (verifyResponse.data.success) {
          console.log('Checkout session verified successfully');
          verificationSuccess = true;
          
          if (verifyResponse.data.subscription) {
            console.log('Subscription data received from verification:', verifyResponse.data.subscription.plan);
            onSuccess(verifyResponse.data.subscription);
          } else {
            // Fallback to fetching user data if subscription not in response
            console.log('No subscription in response, fetching user data');
            await fetchSubscriptionData();
          }
        } else if (verifyResponse.status === 202) {
          // Checkout not completed yet, may need to retry
          console.log('Checkout verification pending, retrying in 2 seconds...');
          setTimeout(handlePaymentRedirect, 2000);
        }
      } catch (error: any) {
        console.error('Error verifying checkout session:', error.message);
      }
      
      // If verification failed, try direct processing
      if (!verificationSuccess) {
        // First check if we have checkout data in component state
        let checkoutIdToProcess = checkoutData?.id;
        
        // If not available in state, try localStorage
        if (!checkoutIdToProcess) {
          const storedCheckoutId = localStorage.getItem('pendingCheckoutId');
          if (storedCheckoutId) {
            console.log('Found checkout ID in localStorage:', storedCheckoutId);
            checkoutIdToProcess = storedCheckoutId;
          }
        }
        
        // If we found a checkout ID, try to process it
        if (checkoutIdToProcess) {
          console.log('Verification failed, trying direct processing with checkout ID:', checkoutIdToProcess);
          directProcessingSuccess = await processCheckoutDirectly(checkoutIdToProcess);
          
          // If successful, clean up localStorage
          if (directProcessingSuccess) {
            localStorage.removeItem('pendingCheckoutId');
            localStorage.removeItem('pendingCheckoutPlan');
            localStorage.removeItem('pendingCheckoutTimestamp');
          }
        }
      }
      
      // If both approaches failed, try to fetch subscription data as a last resort
      if (!verificationSuccess && !directProcessingSuccess) {
        console.log('All verification approaches failed, fetching subscription data directly');
        await fetchSubscriptionData();
      }
      
      // Close dialog after a delay regardless of result
      setTimeout(() => {
        if (checkoutSuccess) {
          onClose();
        }
      }, 3000);
    } else {
      // Check if there's a stored checkout ID even without URL parameters
      // This helps if the user closed the browser after payment but before redirect
      const storedCheckoutId = localStorage.getItem('pendingCheckoutId');
      const storedTimestamp = localStorage.getItem('pendingCheckoutTimestamp');
      
      if (storedCheckoutId && storedTimestamp) {
        // Only try to recover if the stored checkout is recent (less than 10 minutes old)
        const timestamp = parseInt(storedTimestamp, 10);
        const now = Date.now();
        const minutesSinceCheckout = (now - timestamp) / (1000 * 60);
        
        if (minutesSinceCheckout < 10) {
          console.log('Found recent checkout in localStorage, trying to recover:', {
            checkoutId: storedCheckoutId,
            minutesSinceCheckout
          });
          
          try {
            // Try to process it directly
            const success = await processCheckoutDirectly(storedCheckoutId);
            
            // If successful, clean up localStorage
            if (success) {
              localStorage.removeItem('pendingCheckoutId');
              localStorage.removeItem('pendingCheckoutPlan');
              localStorage.removeItem('pendingCheckoutTimestamp');
            }
          } catch (error) {
            console.error('Error processing stored checkout:', error);
          }
        } else {
          // Clean up old checkout data
          localStorage.removeItem('pendingCheckoutId');
          localStorage.removeItem('pendingCheckoutPlan');
          localStorage.removeItem('pendingCheckoutTimestamp');
        }
      }
    }
  }, [apiUrl, checkoutData, checkoutSuccess, fetchSubscriptionData, onClose, onSuccess, processCheckoutDirectly]);

  // Run the payment redirect handler both on mount and when the component is opened
  useEffect(() => {
    if (open) {
      handlePaymentRedirect();
    }
  }, [open, handlePaymentRedirect]);

  // Check URL parameters on component mount, regardless of dialog state
  useEffect(() => {
    handlePaymentRedirect();
  }, [handlePaymentRedirect]);
  
  // Create checkout
  const handleCreateCheckout = async () => {
    if (!selectedPlan) {
      setCheckoutError('Please select a plan first');
      return;
    }

    setIsProcessing(true);
    setCheckoutError(null);
    
    try {
      // Use subscription-checkout endpoint for recurring billing
      const response = await axios.post(
        `${apiUrl}/api/payments/subscription-checkout`,
        { plan: selectedPlan },
        { withCredentials: true }
      );
      
      if (response.data.success && (response.data.checkoutId || response.data.redirectUrl)) {
        const checkoutData = {
          id: response.data.checkoutId,
          redirectUrl: response.data.redirectUrl || response.data.payment_url || response.data.paymentPageUrl,
          paymentPageUrl: response.data.paymentPageUrl || response.data.redirectUrl,
          status: 'created'
        };
        
        // Save checkout data to state
        setCheckoutData(checkoutData);
        
        // Save checkout ID to localStorage for recovery
        localStorage.setItem('pendingCheckoutId', checkoutData.id);
        localStorage.setItem('pendingCheckoutPlan', selectedPlan);
        localStorage.setItem('pendingCheckoutTimestamp', Date.now().toString());
        
        // Debug info
        console.log('Subscription checkout created successfully:', {
          id: checkoutData.id,
          redirectUrl: checkoutData.redirectUrl
        });
        
        // Get the correct URL from the response
        const redirectUrl = checkoutData.redirectUrl || checkoutData.paymentPageUrl;
        
        if (!redirectUrl) {
          setCheckoutError('No payment URL returned from server. Please try again or contact support.');
          setIsProcessing(false);
          return;
        }
        
        // Show redirecting UI
        setIsRedirecting(true);
        
        // Delay slightly to ensure state updates happen before redirect
        setTimeout(() => {
          try {
            // Try to use location.href instead of window.open
            window.location.href = redirectUrl;
          } catch (redirectError) {
            console.error('Failed to redirect:', redirectError);
            
            // Fallback approach
            const newWindow = window.open(redirectUrl, '_self');
            
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              // Popup was blocked or failed to open
              setCheckoutError('Unable to open payment page. Please disable popup blockers and try again.');
              setIsProcessing(false);
              setIsRedirecting(false);
            }
          }
        }, 1000); // Longer delay to allow seeing the redirecting message
      } else {
        console.error('Subscription checkout creation failed:', response.data);
        
        // Handle case where user needs to add payment method first
        if (response.data.needsPaymentMethod) {
          setCheckoutError('Please add a payment method before subscribing.');
        } else {
          setCheckoutError(response.data.message || 'Failed to create subscription checkout. Please try again.');
        }
        
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error('Subscription checkout creation error:', error);
      setCheckoutError(
        error.response?.data?.message || 
        'Failed to create subscription checkout. Please try again.'
      );
      setIsProcessing(false);
    }
  };
  
  // Handle manual redirect
  const handleManualRedirect = () => {
    if (checkoutData?.redirectUrl) {
      window.location.href = checkoutData.redirectUrl;
    } else if (checkoutData?.paymentPageUrl) {
      window.location.href = checkoutData.paymentPageUrl;
    } else {
      setCheckoutError('Payment URL not available. Please try again.');
    }
  };
  
  // Show manual redirect button after delay
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isRedirecting) {
      timer = setTimeout(() => {
        setShowManualRedirect(true);
      }, 5000); // Show manual redirect button after 5 seconds
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isRedirecting]);
  
  const handleCloseDialog = () => {
    setCheckoutError(null);
    setIsRedirecting(false);
    setShowManualRedirect(false);
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {checkoutSuccess ? 'Payment Successful!' : 'Subscribe to ' + (selectedPlan === 'basic' ? 'Basic' : 'Enterprise') + ' Plan'}
      </DialogTitle>
      
      <DialogContent>
        {checkoutSuccess ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Thank you for your subscription!
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Your subscription has been activated successfully.
            </Typography>
          </Box>
        ) : (
          <Box>
            {checkoutError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {checkoutError}
              </Alert>
            )}
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              You are subscribing to the <strong>{selectedPlan === 'basic' ? 'Basic' : 'Enterprise'} Plan</strong> for{' '}
              <strong>${selectedPlan === 'basic' ? '20' : '40'} per month</strong>.
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              This is a recurring monthly subscription. Your card will be charged automatically 
              each month until you cancel. You can cancel anytime from your account dashboard.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>
              {selectedPlan === 'basic' ? (
                'Basic Plan includes: 1 chatbot, standard support'
              ) : (
                'Enterprise Plan includes: 3 chatbots, priority support, advanced analytics'
              )}
            </Typography>
            
            {isProcessing ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={30} />
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {isRedirecting 
                    ? 'Redirecting to payment page...' 
                    : 'Creating subscription checkout...'}
                </Typography>
                
                {isRedirecting && showManualRedirect && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      If you're not automatically redirected, please check your popup blocker 
                      settings or click the button below.
                    </Alert>
                    <Button 
                      variant="contained"
                      color="primary"
                      onClick={handleManualRedirect}
                    >
                      Go to Payment Page
                    </Button>
                  </Box>
                )}
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        {!isProcessing && !checkoutSuccess && (
          <>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCheckout} 
              variant="contained" 
              color="primary"
              disabled={isProcessing}
            >
              Proceed to Subscription
            </Button>
          </>
        )}
        {checkoutSuccess && (
          <Button onClick={handleCloseDialog} color="primary">
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RapydCheckoutForm; 